import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, defer, from, map, switchMap, catchError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { getAuth } from 'firebase/auth';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

import { firebaseApp, db } from '../gen2ai/fire-init';

export type DuckResponse = {
  uid: string;
  media_id: string;
  prompt_type: string;
  timestamp: string;

  description: string;
  caption: string;

  image: string; // base64 for UI preview only
  image_format: string;
  model_id: string;
};

export type MediaUploadContext = {
  uid: string;
  mediaId: string;
  storagePath: string;
  downloadUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

@Injectable({ providedIn: 'root' })
export class ImageDescriptionService {
  // Single active lambda endpoint
  private readonly sexy35 = 'https://twhxitkv74jgljpvofys7xq3ga0mvxra.lambda-url.us-east-1.on.aws/';

  private readonly ANON_UID_STORAGE_KEY = 'clarichat_anon_uid';
  private storage = getStorage(firebaseApp);

  constructor(private http: HttpClient) {}

  // ---------------- UID ----------------
  private getOrCreateAnonUid(): string {
    try {
      const existing = window?.localStorage?.getItem(this.ANON_UID_STORAGE_KEY);
      if (existing) return existing;

      const anon = 'anon-' + uuidv4();
      window?.localStorage?.setItem(this.ANON_UID_STORAGE_KEY, anon);
      return anon;
    } catch {
      return 'anon-' + uuidv4();
    }
  }

  private resolveUid(): string {
    try {
      const auth = getAuth(firebaseApp);
      return auth.currentUser?.uid || this.getOrCreateAnonUid();
    } catch {
      return this.getOrCreateAnonUid();
    }
  }

  // ---------------- Base64 helper ----------------
  private fileToBase64(file: File): Observable<string> {
    return new Observable<string>((observer) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        if (!reader.result) {
          observer.error(new Error('Could not read file as base64'));
          return;
        }

        const dataUrl = reader.result.toString();
        const parts = dataUrl.split(',');

        if (parts.length < 2) {
          observer.error(new Error('Invalid DataURL format'));
          return;
        }

        observer.next(parts[1]);
        observer.complete();
      };

      reader.onerror = (err) => observer.error(err);
      reader.readAsDataURL(file);

      return () => {
        try {
          reader.abort();
        } catch {}
      };
    });
  }

  // ---------------- Firebase upload + doc create ----------------
  private async createAndUploadUserMedia(file: File, source: string): Promise<MediaUploadContext> {
    const uid = this.resolveUid();
    const mediaId = uuidv4();

    const fileName = file?.name || 'upload';
    const contentType = file?.type || 'application/octet-stream';
    const size = typeof file?.size === 'number' ? file.size : 0;

    const extFromName = (fileName.split('.').pop() || '').toLowerCase();
    const extFromType =
      contentType.includes('png') ? 'png' :
      contentType.includes('webp') ? 'webp' :
      contentType.includes('gif') ? 'gif' :
      'jpg';

    const ext = extFromName && extFromName.length <= 5 ? extFromName : extFromType;

    const storagePath = `media/${uid}/${mediaId}.${ext}`;
    const sref = storageRef(this.storage, storagePath);

    const mediaDocRef = doc(db, 'users', uid, 'media', mediaId);

    await setDoc(
      mediaDocRef,
      {
        uid,
        mediaId,
        source,
        fileName,
        contentType,
        size,
        storagePath,
        status: 'uploading',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );

    await uploadBytes(sref, file, { contentType });
    const downloadUrl = await getDownloadURL(sref);

    await updateDoc(mediaDocRef, {
      downloadUrl,
      status: 'processing',
      updatedAt: serverTimestamp()
    });

    return { uid, mediaId, storagePath, downloadUrl, fileName, contentType, size };
  }

  // ---------------- Lambda call (BODY ONLY) ----------------
  private callLambdaBodyOnly(file: File, promptType: string, uid: string, mediaId: string): Observable<DuckResponse> {
    return this.fileToBase64(file).pipe(
      switchMap((base64Image) => {
        const body = {
          image: base64Image,
          uid,
          media_id: mediaId,
          prompt_type: String(promptType ?? '').toLowerCase()
        };

        return this.http.post<DuckResponse>(this.sexy35, body);
      }),
      catchError((error: HttpErrorResponse) => {
        const msg = this.describeHttpError(error);
        console.error('[ImageDescriptionService] Lambda error:', msg, error);
        return throwError(() => new Error(msg));
      })
    );
  }

  // ---------------- Primary public call ----------------
  fetchDescription(image: File, descriptionType: string = 'description'): Observable<DuckResponse> {
    const uid = this.resolveUid();
    const mediaId = uuidv4();
    return this.callLambdaBodyOnly(image, descriptionType, uid, mediaId);
  }

  // ---------------- Backward-compatible aliases ----------------
  // These all intentionally route to the single sexy35 endpoint.
  fetch30(image: File, descriptionType: string = 'description'): Observable<DuckResponse> {
    return this.fetchDescription(image, descriptionType);
  }

  fetch34(image: File, descriptionType: string = 'description'): Observable<DuckResponse> {
    return this.fetchDescription(image, descriptionType);
  }

  fetch35(image: File, descriptionType: string = 'description'): Observable<DuckResponse> {
    return this.fetchDescription(image, descriptionType);
  }

  fetch37(image: File, descriptionType: string = 'description'): Observable<DuckResponse> {
    return this.fetchDescription(image, descriptionType);
  }

  // ---------------- Public method to upload media only ----------------
  async uploadMediaOnly(file: File, source: string): Promise<MediaUploadContext> {
    return this.createAndUploadUserMedia(file, source);
  }

  // ---------------- Public method to call lambda with already-uploaded context ----------------
  // lambdaUrl is retained only for compatibility with existing callers.
  callLambdaWithContext(
    lambdaUrl: string,
    file: File,
    promptType: string,
    ctx: MediaUploadContext
  ): Observable<DuckResponse> {
    void lambdaUrl;

    return this.callLambdaBodyOnly(file, promptType, ctx.uid, ctx.mediaId).pipe(
      switchMap((resp) =>
        from(this.storeReturnedMetadata(ctx, promptType, 'sonnet35', resp)).pipe(
          map(() => resp)
        )
      )
    );
  }

  private describeHttpError(err: HttpErrorResponse): string {
    const status = err?.status;

    if (status === 0) {
      return 'CORS/preflight blocked (status 0). Ensure Function URL CORS is enabled and OPTIONS is allowed.';
    }

    const body = err?.error;

    if (typeof body === 'string' && body.trim()) {
      return `[${status}] ${body}`;
    }

    if (body && typeof body === 'object') {
      const m = (body.error || body.message || body.Message || JSON.stringify(body)).toString();
      return `[${status}] ${m}`;
    }

    return `[${status}] ${err?.message || 'Request failed'}`;
  }

  // ---------------- Store returned metadata under uid/mediaId ----------------
  private async storeReturnedMetadata(
    ctx: MediaUploadContext,
    promptType: string,
    variantKey: string,
    resp: DuckResponse
  ): Promise<void> {
    const mediaDocRef = doc(db, 'users', ctx.uid, 'media', ctx.mediaId);
    const field = this.mapTypeToField(promptType);

    const updatePayload: any = {
      updatedAt: serverTimestamp(),
      status: 'ready',
      lastAi: {
        variant: variantKey,
        field,
        model_id: resp.model_id,
        prompt_type: resp.prompt_type,
        timestamp: resp.timestamp
      }
    };

    const text = (resp.description || '').toString().trim();
    if (text) {
      updatePayload[`ai.${variantKey}.${field}`] = text;
    }

    if (resp.caption && resp.caption.trim()) {
      updatePayload[`ai.${variantKey}.caption`] = resp.caption.trim();
    }

    updatePayload[`aiMeta.${variantKey}`] = {
      model_id: resp.model_id,
      image_format: resp.image_format,
      prompt_type: resp.prompt_type,
      timestamp: resp.timestamp
    };

    await updateDoc(mediaDocRef, updatePayload);
  }

  private mapTypeToField(selectedType: string): string {
    const v = (selectedType || '').trim().toLowerCase();

    if (v === 'caption' || v === '3') return 'caption';
    if (v === 'summary' || v === '2' || v === 'short') return 'shortDescription';
    if (v === 'description' || v === '1') return 'description';

    return v.replace(/\s+/g, '_') || 'description';
  }

  // ============================================================
  // Upload to Firebase + call Lambda + store metadata in Firestore
  // ============================================================
  // variantKey and lambdaUrl are retained only for compatibility.
  uploadAndDescribeAndStore(opts: {
    file: File;
    promptType: string;
    variantKey?: string;
    source?: string;
    lambdaUrl?: string;
  }): Observable<{ ctx: MediaUploadContext; resp: DuckResponse }> {
    const source = opts.source || 'quackstudio';
    const variantKey = opts.variantKey || 'sonnet35';
    void opts.lambdaUrl;

    return defer(() => from(this.createAndUploadUserMedia(opts.file, source))).pipe(
      switchMap((ctx) =>
        this.callLambdaBodyOnly(opts.file, opts.promptType, ctx.uid, ctx.mediaId).pipe(
          switchMap((resp) =>
            from(this.storeReturnedMetadata(ctx, opts.promptType, variantKey, resp)).pipe(
              map(() => ({ ctx, resp }))
            )
          )
        )
      )
    );
  }

  // ---------------- convenience wrapper ----------------
  urlForVariant(_variantKey: string): string {
    return this.sexy35;
  }
}